import { useMemo, useContext } from 'react';
import memoize from 'lodash/memoize';
import { isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';

import type { CustomizableMenuItemData } from '@atlassian/customization-modal';
import { type fnGetFilteredProductLinks } from '@atlassian/global-shortcuts';

import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import {
	COMPANY_HUB,
	CONFLUENCE_PEOPLE_DIRECTORY_SEARCH,
	HOME,
	TEMPLATE_GALLERY,
} from '@confluence/named-routes/entry-points/routes.ts';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext.ts';
import { getCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';
import { SYSTEM_HEADER_LEFT } from '@confluence/web-item-location/entry-points/webFragmentLocations.ts';
import { useWebItemLocation } from '@confluence/web-item-location/entry-points/useWebItemLocation.ts';
import { fg } from '@confluence/feature-gating';
import { isTeamsAppEnabled } from '@confluence/teams-app-config/entry-points/isTeamsAppEnabled.ts';

// In preperation for upcoming platform adoption of side navigation
// https://hello.jira.atlassian.cloud/browse/NAVX-908
import { useShortcutVisibilityOverrides } from '../PlatformNavigation/AppShortcuts/hooks/useShortcutVisibilityOverrides.tsx';
import { useLicensedProducts } from '../PlatformNavigation/AppShortcuts/hooks/useLicensedProducts.tsx';
import { useFallbackUrls } from '../PlatformNavigation/AppShortcuts/hooks/useFallbackUrls.tsx';
import type { MenuId } from '../PlatformNavigation/PlatformNavigationTypes.ts';

export type MenuItemVisibilityOverrides = Partial<Record<MenuId, boolean>>;
export type GlobalMenuItemConfig = Pick<MenuItemConfig, 'menuId' | 'isCustomizable'>[];
export type MoreMenuItemConfig = Pick<
	MenuItemConfig,
	'menuId' | 'isCustomizable' | 'href' | 'isAppShortcut'
>[];
export type AppMenuItemConfig = Pick<
	MenuItemConfig,
	'menuId' | 'isCustomizable' | 'isAppShortcut'
>[];

export type PlatformCustomizationItem = {
	menuId: MenuId;
	visible: boolean;
	isAppShortcut?: boolean;
};

type MenuItemConfig = {
	menuId: MenuId;
	visibility: boolean;
	defaultVisibility: boolean;
	authenticationLevelRequired:
		| AccessStatus.ANONYMOUS_ACCESS
		| AccessStatus.EXTERNAL_COLLABORATOR_ACCESS
		| AccessStatus.EXTERNAL_SHARE_ACCESS
		| AccessStatus.LICENSED_ADMIN_ACCESS
		| AccessStatus.LICENSED_USE_ACCESS
		| AccessStatus.NOT_PERMITTED
		| AccessStatus.UNLICENSED_AUTHENTICATED_ACCESS;
	isCustomizable: boolean;
	href?: string;
	isAppShortcut?: boolean;
	apsKey?: string;
};

type DefaultMenuItemConfig = Pick<
	MenuItemConfig,
	| 'defaultVisibility'
	| 'authenticationLevelRequired'
	| 'isCustomizable'
	| 'href'
	| 'isAppShortcut'
	| 'apsKey'
>;

const AuthenticationEnumMap = {
	//0 least privileged, 3 most privileged
	[AccessStatus.ANONYMOUS_ACCESS]: 0,
	[AccessStatus.EXTERNAL_COLLABORATOR_ACCESS]: 1,
	[AccessStatus.EXTERNAL_SHARE_ACCESS]: 0,
	[AccessStatus.LICENSED_ADMIN_ACCESS]: 3,
	[AccessStatus.LICENSED_USE_ACCESS]: 2,
	[AccessStatus.NOT_PERMITTED]: 0,
	[AccessStatus.UNLICENSED_AUTHENTICATED_ACCESS]: 1,
};

// Default configuration for all possible top-level items in Confluence Global Nav
const defaultMenuConfiguration: Record<MenuId, DefaultMenuItemConfig> = {
	customHome: {
		defaultVisibility: true,
		authenticationLevelRequired: AccessStatus.ANONYMOUS_ACCESS,
		isCustomizable: false,
	},
	yourWork: {
		defaultVisibility: true,
		authenticationLevelRequired: AccessStatus.LICENSED_USE_ACCESS,
		isCustomizable: false,
	},
	companyHub: {
		defaultVisibility: true,
		authenticationLevelRequired: AccessStatus.LICENSED_USE_ACCESS,
		isCustomizable: false,
	},
	recent: {
		defaultVisibility: true,
		authenticationLevelRequired: AccessStatus.UNLICENSED_AUTHENTICATED_ACCESS,
		isCustomizable: true,
	},
	starred: {
		defaultVisibility: true,
		authenticationLevelRequired: AccessStatus.UNLICENSED_AUTHENTICATED_ACCESS,
		isCustomizable: true,
	},
	spaces: {
		defaultVisibility: true,
		authenticationLevelRequired: AccessStatus.ANONYMOUS_ACCESS,
		isCustomizable: true,
	},
	apps: {
		defaultVisibility: true,
		authenticationLevelRequired: AccessStatus.ANONYMOUS_ACCESS,
		isCustomizable: true,
	},
	goals: {
		//todo: where does this go?
		defaultVisibility: false,
		authenticationLevelRequired: AccessStatus.LICENSED_USE_ACCESS,
		isCustomizable: true,
		href: '#',
		apsKey: 'GOALS',
	},
	topics: {
		//todo: where does this go?
		defaultVisibility: false,
		authenticationLevelRequired: AccessStatus.ANONYMOUS_ACCESS,
		isCustomizable: true,
		href: '#',
	},
	teams: {
		defaultVisibility: true,
		authenticationLevelRequired: AccessStatus.LICENSED_USE_ACCESS,
		isCustomizable: true,
		href: CONFLUENCE_PEOPLE_DIRECTORY_SEARCH.toUrl(),
	},
	drafts: {
		defaultVisibility: false,
		authenticationLevelRequired: AccessStatus.LICENSED_USE_ACCESS,
		isCustomizable: true,
		href: HOME.toUrl({ view: 'drafts' }),
	},
	tasks: {
		defaultVisibility: false,
		authenticationLevelRequired: AccessStatus.LICENSED_USE_ACCESS,
		isCustomizable: true,
		href: HOME.toUrl({ view: 'tasks' }),
	},
	templates: {
		defaultVisibility: false,
		authenticationLevelRequired: AccessStatus.ANONYMOUS_ACCESS,
		isCustomizable: true,
		href: TEMPLATE_GALLERY.toUrl(),
	},
	companyHubShortcut: {
		defaultVisibility: true,
		authenticationLevelRequired: AccessStatus.LICENSED_USE_ACCESS,
		isCustomizable: true,
		isAppShortcut: true,
		href: COMPANY_HUB.toUrl(),
	},
	jiraShortcut: {
		defaultVisibility: true,
		authenticationLevelRequired: AccessStatus.LICENSED_USE_ACCESS,
		isCustomizable: true,
		isAppShortcut: true,
		apsKey: 'JIRA_SOFTWARE',
	},
	loomShortcut: {
		defaultVisibility: true,
		authenticationLevelRequired: AccessStatus.LICENSED_USE_ACCESS,
		isCustomizable: true,
		isAppShortcut: true,
		apsKey: 'LOOM',
	},
	teamsShortcut: {
		defaultVisibility: true,
		authenticationLevelRequired: AccessStatus.LICENSED_USE_ACCESS,
		isCustomizable: true,
		isAppShortcut: true,
		apsKey: 'TEAMS',
	},
	customize: {
		defaultVisibility: false,
		authenticationLevelRequired: AccessStatus.LICENSED_USE_ACCESS,
		isCustomizable: false,
	},
};

const defaultPlatformCustomizationList: PlatformCustomizationItem[] = Object.entries(
	defaultMenuConfiguration,
)
	.filter(([, { isCustomizable }]) => isCustomizable)
	.map(([menuId, { defaultVisibility, isAppShortcut }]) => ({
		menuId: menuId as MenuId,
		visible: defaultVisibility,
		isAppShortcut: isAppShortcut || false,
	}));

const mergeCustomizationsWithDefaults = memoize(
	(platformCustomizations?: PlatformCustomizationItem[]) => {
		const validatedCustomizations: PlatformCustomizationItem[] = [];
		const customizedMenuIds = new Set();

		platformCustomizations?.forEach((item) => {
			if (defaultMenuConfiguration[item.menuId as MenuId]) {
				validatedCustomizations.push(item);
				customizedMenuIds.add(item.menuId);
			}
		});

		// These items always appear first
		const finalMenuItems: PlatformCustomizationItem[] = [
			{ menuId: 'customHome', visible: true },
			{ menuId: 'yourWork', visible: true },
		];

		// Add any customized menu items
		validatedCustomizations.forEach((item) => {
			finalMenuItems.push(item);
		});

		// Add any default items that were not customized
		defaultPlatformCustomizationList.forEach((item) => {
			if (!customizedMenuIds.has(item.menuId)) {
				finalMenuItems.push(item);
			}
		});

		// Company Hub is an app shortcut.
		finalMenuItems.push({ menuId: 'companyHub', visible: true });

		// Customize always appears last
		finalMenuItems.push({ menuId: 'customize', visible: true });

		return finalMenuItems;
	},
);

const createMenuItem = (menuId: MenuId, config: DefaultMenuItemConfig) => ({
	menuId,
	isCustomizable: config.isCustomizable,
	...(config.href && { href: config.href }),
	...(config.isAppShortcut && { isAppShortcut: config.isAppShortcut }),
});

const createMenuItemNext = (menuId: MenuId, config: DefaultMenuItemConfig, visible: boolean) => ({
	menuId,
	visible,
	isNotCustomizable: !config.isCustomizable,
	isNotToggleable: !config.isCustomizable,
	...(config.href && { href: config.href }),
	...(config.isAppShortcut && { isAppShortcut: config.isAppShortcut }),
});

// create lists of menu items based on specific filters and conditions
export const generateMenuItemLists = (
	showCustomization: boolean,
	accessStatus?: AccessStatus | null,
	platformCustomizations?: PlatformCustomizationItem[],
	visibilityOverrides?: MenuItemVisibilityOverrides,
	getFilteredProductLinks?: fnGetFilteredProductLinks,
	fallbackUrls?: Record<string, string>,
) => {
	const globalMenuItems: GlobalMenuItemConfig = [];
	const moreMenuItems: MoreMenuItemConfig = [];
	const appShortcutMenuItems: CustomizableMenuItemData[] = [];
	const allMenuItems: CustomizableMenuItemData[] = [];
	const orderedItems = mergeCustomizationsWithDefaults(platformCustomizations);
	const customizeItemId: MenuId = 'customize';

	orderedItems.forEach((item) => {
		const menuId = item.menuId as MenuId;
		const defaultConfig = defaultMenuConfiguration[menuId];
		const accessStatusLevel = (accessStatus && AuthenticationEnumMap[accessStatus]) || 0; //if accessStatus is null, we default to anon level access

		if (
			//check if auth level is equal to or greater than what's required for each menu item
			AuthenticationEnumMap[defaultConfig.authenticationLevelRequired] <= accessStatusLevel &&
			visibilityOverrides?.[menuId] !== false &&
			menuId !== customizeItemId &&
			!defaultConfig.isAppShortcut
		) {
			const menuItem = createMenuItem(menuId, defaultConfig);
			(item.visible ? globalMenuItems : moreMenuItems).push(menuItem);
			allMenuItems.push(createMenuItemNext(menuId, defaultConfig, item.visible));
		}

		// APP SHORTCUTS
		if (visibilityOverrides?.[menuId] && defaultConfig.isAppShortcut) {
			const pushConfig = { ...defaultConfig };

			if (getFilteredProductLinks && defaultConfig.apsKey) {
				pushConfig.href =
					getFilteredProductLinks(defaultConfig.apsKey)?.links[0]?.url ?? fallbackUrls?.[menuId];
			}

			if ((pushConfig.apsKey && pushConfig.href) || !pushConfig.apsKey) {
				appShortcutMenuItems.push(createMenuItemNext(item.menuId, pushConfig, item.visible));

				if (!item.visible) {
					moreMenuItems.push(createMenuItem(item.menuId, pushConfig));
				}
			}
		}
	});

	// customize sidebar item should be the last item of the more items menu
	// unless there are no items, then it should be in the global menu items
	if (showCustomization && visibilityOverrides?.[customizeItemId] !== false) {
		const customizeItem = createMenuItem(
			customizeItemId,
			defaultMenuConfiguration[customizeItemId],
		);
		(moreMenuItems.length === 0 ? globalMenuItems : moreMenuItems).push(customizeItem);
	}

	return {
		globalMenuItems,
		moreMenuItems,
		allMenuItems,
		appShortcutMenuItems,
	};
};

export const useMenuItems = (
	showCustomization: boolean,
	accessStatus?: AccessStatus | null,
	platformCustomizations?: PlatformCustomizationItem[],
	getFilteredProductLinks?: fnGetFilteredProductLinks,
) => {
	const { loading: webItemsLoading, webItems } = useWebItemLocation({
		location: SYSTEM_HEADER_LEFT,
		allowedWebItems: [SPACE_DIRECTORY_KEY, PEOPLE_DIRECTORY_KEY],
	});
	const fallbackUrls = useFallbackUrls();
	const { hasJira, hasLoom } = useLicensedProducts();
	const { homepageUri } = useContext(SPAViewContext);
	const { isPeopleVisible, isCompanyHubVisible } = useShortcutVisibilityOverrides();

	const teamsAppEnabled = isTeamsAppEnabled();
	// environment-aware enablement for Forge Apps sidebar via feature encoding
	const shouldHideAppsSideBarInIsolatedCloud = !isFeatureEnabled(
		'confluence',
		'confluence-forge-apps-sidebar',
	);
	const isSpacesVisible =
		!webItemsLoading && !!webItems.find((item) => item.completeKey === SPACE_DIRECTORY_KEY);

	const isCompanyHubSetAsCustomHome =
		isCompanyHubVisible && homepageUri.includes(getCompanyHubSpaceKey());
	const isCustomHomeVisible: boolean =
		!isCompanyHubSetAsCustomHome && !['/spa.action', '', 'homepageUri'].includes(homepageUri);

	return useMemo(() => {
		// remove elements from the returned data structures based on application state and override defaults and customization store
		// eslint-disable-next-line confluence-feature-gating/inline-usage
		const visibilityOverrides: MenuItemVisibilityOverrides = {
			customHome: isCustomHomeVisible,
			companyHub: isCompanyHubVisible && !fg('nav4_global_links_confluence'),
			spaces: isSpacesVisible,
			teams: isPeopleVisible && !teamsAppEnabled,
			topics: false, // todo: remove when platform tells us what topics does / goes
			goals: false, // todo: remove when platform tells us what goals does / goes
			templates: true,
			drafts: true,
			apps: !shouldHideAppsSideBarInIsolatedCloud,
			tasks: true,
			customize: true,
			companyHubShortcut: isCompanyHubVisible && fg('nav4_global_links_confluence'),
			jiraShortcut: hasJira && fg('nav4_global_links_confluence'),
			loomShortcut: hasLoom && fg('nav4_global_links_confluence'),
			teamsShortcut: isPeopleVisible && teamsAppEnabled,
		};

		return generateMenuItemLists(
			showCustomization,
			accessStatus,
			platformCustomizations,
			visibilityOverrides,
			getFilteredProductLinks,
			fallbackUrls,
		);
	}, [
		isCustomHomeVisible,
		isCompanyHubVisible,
		isSpacesVisible,
		isPeopleVisible,
		accessStatus,
		shouldHideAppsSideBarInIsolatedCloud,
		showCustomization,
		platformCustomizations,
		getFilteredProductLinks,
		teamsAppEnabled,
		fallbackUrls,
		hasJira,
		hasLoom,
	]);
};

export const SPACE_DIRECTORY_KEY =
	'com.atlassian.confluence.plugins.confluence-space-ia:header-spaces-link';
export const PEOPLE_DIRECTORY_KEY =
	'com.atlassian.confluence.plugins.confluence-space-ia:header-people-link';
